<script setup lang="ts">
import {
  PriceCalendar,
  PriceCalendarTileType,
} from '@apps/main/new-desktop/modules/priceCalendar';

import {
  AppButton,
  AppFlicker,
  AppMessageBox,
  AppStandardModal,
  AppSvgIcon,
} from '@libs/ui';

import type { PeekPriceModalCalendar } from '../../models';

interface PeekPriceModalMobileProps {
  hotelName: string | undefined;
  calendar: PeekPriceModalCalendar;
}

const props = defineProps<PeekPriceModalMobileProps>();

interface PeekPriceModalEmits {
  (eventName: 'cancel'): void;
  (eventName: 'confirm'): void;
  (eventName: 'selectDate', date: Date): void;
  (eventName: 'selectMonth', date: Date): void;
}

const emit = defineEmits<PeekPriceModalEmits>();
</script>

<template>
  <AppStandardModal
    footer-padding="small"
    :close-button-aria-label="$t('a11y.close_panel')"
    @close="emit('cancel')"
    @confirm="emit('confirm')"
  >
    <template #body>
      <div class="peek-price-mobile-body">
        <AppMessageBox
          :text="$t('planner.calendar_shows_date_price')"
          message-type="brand-lagoon"
          class="message-box"
        >
          <template #left-icon>
            <AppSvgIcon name="lightbulb" />
          </template>
        </AppMessageBox>

        <div class="prices-for-hotel-text">
          {{ $t('planner.prices_in_calendar') }}
          <span class="hotel-name-text">{{ props.hotelName }}.</span>
        </div>

        <PriceCalendar
          v-if="props.calendar.priceCalendarState.anyResultsLoaded()"
          :selected-month="props.calendar.selectedMonth"
          :months="props.calendar.months"
          :price-calendar-state="props.calendar.priceCalendarState"
          :allow-selection-for="[
            PriceCalendarTileType.CurrentMonthAvailableDay,
          ]"
          @select-month="emit('selectMonth', $event)"
          @select-date="emit('selectDate', $event)"
        />
        <AppFlicker
          v-else
          height="spacing-32"
          width="spacing-80"
          border-radius="border-radius-12"
          class="flicker"
        />
      </div>
    </template>

    <template #summary>
      <div class="peek-price-mobile-footer">
        <AppButton
          type="secondary"
          state="enabled"
          size="medium"
          :wide="true"
          @click="emit('cancel')"
        >
          {{ $t('planner.close') }}
        </AppButton>
        <AppButton
          type="primary"
          state="enabled"
          size="medium"
          :wide="true"
          @click="emit('confirm')"
        >
          {{ $t('planner.select') }}
        </AppButton>
      </div>
    </template>
  </AppStandardModal>
</template>

<style lang="scss" scoped>
.peek-price-mobile-body {
  width: 100%;
  height: 100%;

  display: grid;
  grid-template-rows: min-content min-content 1fr;

  .message-box {
    margin-bottom: var(--spacing-12);
  }

  .prices-for-hotel-text {
    margin-bottom: var(--spacing-12);

    @include font-label-m-light;
    color: var(--color-neutral-70);

    .hotel-name-text {
      @include font-label-m-bold;
      color: var(--color-neutral-70);
    }
  }

  .flicker {
    margin-top: var(--spacing-24);
  }
}

.peek-price-mobile-footer {
  width: 100%;
  display: flex;
  gap: var(--spacing-12);
}
</style>
